/**
 * Venue Time Formatters Hook
 * @description Hook that automatically uses current venue timezone from Redux
 */

import { useSelector } from 'react-redux';
import { useCallback } from 'react';
import { selectCurrentVenue } from '../../../redux/slices/currentUserSlice';
import {
  formatTimeInVenueTimezone,
  formatTimeRangeInVenueTimezone,
  formatShiftTimeInVenueTimezone,
  formatDateInVenueTimezone,
  isVenueTodayDate,
  getVenueTimezone,
} from '../../venueTimeFormatters';

/**
 * Hook that provides formatters using current venue timezone
 * @description These formatters automatically use the current venue's timezone without passing venue manually
 * @returns {Object} - Object containing all formatters pre-configured with current venue
 */
export const useVenueTimeFormatters = () => {
  const currentVenue = useSelector(selectCurrentVenue);

  // Memoize formatters to prevent unnecessary re-renders
  const formatTime = useCallback((time) => formatTimeInVenueTimezone(time, currentVenue), [currentVenue]);

  const formatTimeRange = useCallback(
    (startTime, endTime) => formatTimeRangeInVenueTimezone(startTime, endTime, currentVenue),
    [currentVenue]
  );

  const formatShiftTime = useCallback((shift) => formatShiftTimeInVenueTimezone(shift, currentVenue), [currentVenue]);

  const formatDate = useCallback(
    (date, options) => formatDateInVenueTimezone(date, currentVenue, options),
    [currentVenue]
  );

  const isToday = useCallback((date) => isVenueTodayDate(date, currentVenue), [currentVenue]);

  return {
    // Time formatters that automatically use current venue timezone
    formatTime,
    formatTimeRange,
    formatShiftTime,
    formatDate,
    isToday,

    // Expose current venue info
    currentVenue,
    venueTimezone: getVenueTimezone(currentVenue),
  };
};

/**
 * Hook that provides just the time formatters (most commonly used)
 * @description Convenience hook for components that only need time formatting
 * @returns {Object} - Object containing time formatting functions
 */
export const useTimeFormatters = () => {
  const { formatTime, formatTimeRange, formatShiftTime, venueTimezone } = useVenueTimeFormatters();

  // Return memoized functions to prevent re-renders
  return {
    formatTime,
    formatTimeRange,
    formatShiftTime,
    venueTimezone,
  };
};
